import { Fragment } from "react";

import { Anchor } from "@newsela/angelou";
import { isEmpty } from "@s-libs/micro-dash";
import PropTypes from "prop-types";

import ContentStatus from "./ContentStatus";
import {
  selectedLink,
  sidebarLink,
  sidebarList,
  sidebarListContainer,
  sidebarSubMenuListContainer,
  sidebarTitle,
  navContainer,
  relatedOptionsContainerCss,
  relatedOptionsContainerSelectedCss,
} from "./Sidebar.style";

const propTypes = {
  /**
   * Array with the sections and menu options that will be rendered into the sidebar.
   */
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          content: PropTypes.node.isRequired,
          ariaLabel: PropTypes.string.isRequired,
          title: PropTypes.string.isRequired,
          url: PropTypes.string.isRequired,
          relatedOptions: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.string.isRequired,
              content: PropTypes.node.isRequired,
              ariaLabel: PropTypes.string.isRequired,
              title: PropTypes.string.isRequired,
              url: PropTypes.string.isRequired,
              status: PropTypes.object,
            }),
          ),
        }),
      ),
    }),
  ).isRequired,

  /**
   * The id of the selected menu item.
   */
  selectedId: PropTypes.string,

  /**
   * Optional function that will be used to render a custom Anchor.
   */
  __renderCustomLink: PropTypes.func,

  /**
   * Optional function that will be executed when the user selects a menu option.
   */
  onClickCallback: PropTypes.func,
  /**
   * Optional function that will be used to render an Angelou Badge.
   */
  __renderNewBadge: PropTypes.func,
  /**
   * Boolean check to determine if logic to render new badges has completed
   */
  isLoadingNewMenuOption: PropTypes.bool,
};

/**
 * Sidebar is a generic sidebar that includes sections and the options that will be shown.
 * The component receives menuItems that includes the sections and menu options, the selectedId
 * a customRender for the link if is intended to use different anchor and an on click callback.
 */
const Sidebar = ({
  menuItems,
  selectedId,
  __renderCustomLink,
  onClickCallback,
  __renderNewBadge,
  isLoadingNewMenuOption,
}) => {
  return (
    <aside css={navContainer}>
      {menuItems
        .filter((section) => !isEmpty(section.options))
        .map((section) => {
          const hasMenuItems = menuItems && menuItems.length > 0;
          return (
            <Fragment key={section.id}>
              {section.title && <h2 css={sidebarTitle}>{section.title}</h2>}
              {hasMenuItems && (
                <nav aria-label={section.title}>
                  <ol css={sidebarListContainer}>
                    {section.options.map(
                      ({
                        ariaLabel,
                        content,
                        id,
                        title,
                        url,
                        relatedOptions,
                        isNew,
                      }) => {
                        const isSelected = selectedId === id;
                        return (
                          <li css={sidebarList} key={id}>
                            <Anchor
                              __cssFor={{
                                root: isSelected
                                  ? [sidebarLink(isNew), selectedLink]
                                  : sidebarLink(isNew),
                              }}
                              href={url}
                              __renderCustomLink={__renderCustomLink}
                              title={title}
                              ariaProps={{
                                "aria-current": isSelected,
                                "aria-label": ariaLabel,
                              }}
                              dataProps={{
                                "data-qa-selector": `${content} sidebar item`,
                              }}
                              onClick={onClickCallback}
                            >
                              {content}{" "}
                              {isLoadingNewMenuOption
                                ? null
                                : isNew
                                  ? __renderNewBadge()
                                  : null}
                            </Anchor>
                            {relatedOptions && relatedOptions.length > 0 && (
                              <ol css={sidebarSubMenuListContainer}>
                                {relatedOptions.map(
                                  ({
                                    ariaLabel,
                                    content,
                                    id,
                                    title,
                                    url,
                                    status,
                                  }) => {
                                    const isSubMenuSelected = selectedId === id;

                                    return (
                                      <li css={sidebarList} key={id}>
                                        <div
                                          css={
                                            isSubMenuSelected
                                              ? relatedOptionsContainerSelectedCss
                                              : relatedOptionsContainerCss
                                          }
                                        >
                                          <Anchor
                                            __cssFor={{ root: sidebarLink() }}
                                            href={url}
                                            __renderCustomLink={
                                              __renderCustomLink
                                            }
                                            title={title}
                                            ariaProps={{
                                              "aria-current": isSubMenuSelected,
                                              "aria-label": ariaLabel,
                                            }}
                                            onClick={onClickCallback}
                                          >
                                            {content}
                                          </Anchor>
                                          {status && (
                                            <ContentStatus {...status} />
                                          )}
                                        </div>
                                      </li>
                                    );
                                  },
                                )}
                              </ol>
                            )}
                          </li>
                        );
                      },
                    )}
                  </ol>
                </nav>
              )}
            </Fragment>
          );
        })}
    </aside>
  );
};

Sidebar.propTypes = propTypes;

export default Sidebar;
